import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { Id } from "@cvx/_generated/dataModel";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { useState } from "react";

export interface Campaign {
  _id: Id<"campaigns">;
  name?: string;
  description?: string;
}

interface CampaignSelectorProps {
  campaigns: Campaign[] | undefined;
  selectedCampaignId: Id<"campaigns"> | undefined;
  onCampaignChange: (campaignId: Id<"campaigns">) => void;
  campaignsStatus:
    | "CanLoadMore"
    | "LoadingMore"
    | "Exhausted"
    | "LoadingFirstPage";
  loadMoreCampaigns: (numItems: number) => void;
}

export const CampaignSelector = ({
  campaigns,
  selectedCampaignId,
  onCampaignChange,
  campaignsStatus,
  loadMoreCampaigns,
}: CampaignSelectorProps) => {
  const [open, setOpen] = useState(false);
  const selectedCampaign = campaigns?.find((c) => c._id === selectedCampaignId);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (
      scrollHeight - scrollTop <= clientHeight + 50 &&
      campaignsStatus === "CanLoadMore"
    ) {
      loadMoreCampaigns(10);
    }
  };

  return (
    <div className="w-full">
      <Label className="text-sm font-medium mb-2 block">Campaign</Label>
      <Select
        open={open}
        onOpenChange={setOpen}
        value={selectedCampaignId}
        onValueChange={(value) => onCampaignChange(value as Id<"campaigns">)}
      >
        <SelectTrigger className="w-full">
          <SelectValue>
            {selectedCampaign ? (
              <div className="flex items-center justify-between w-full">
                <span>
                  {selectedCampaign.name ||
                    `Campaign ${selectedCampaign._id.slice(-6)}`}
                </span>
              </div>
            ) : (
              "Select a campaign"
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="w-full" onScroll={handleScroll}>
          {campaigns?.map((campaign) => (
            <SelectItem key={campaign._id} value={campaign._id}>
              <div className="flex flex-col items-start w-full">
                <span className="font-medium">
                  {campaign.name || `Campaign ${campaign._id.slice(-6)}`}
                </span>
                {campaign.description && (
                  <span className="text-xs text-muted-foreground truncate max-w-full">
                    {campaign.description}
                  </span>
                )}
              </div>
            </SelectItem>
          ))}
          {campaignsStatus === "LoadingMore" && (
            <div className="flex justify-center p-2">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          )}
        </SelectContent>
      </Select>
    </div>
  );
};
